import type { Building } from "building-types/building/buildingTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { RoomKind } from "building-types/rooms/roomTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { StartSortedWallGaps } from "building-types/walls/wallGap";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall } from "building-types/walls/wallTypes";
import { getOrientedDimLine } from "doc/dimensions";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import type { PlanarGraph } from "geom/planarGraph";
import {
  planarGraphAdjacentHalfEdge,
  planarGraphHalfEdgeEnd,
  planarGraphHalfEdgeLength,
  planarGraphHalfEdgeNormal,
  planarGraphHalfEdgeStart,
  planarGraphHalfEdgeTangent,
  planarGraphHalfEdgeToLine2,
  planarGraphNextHalfEdgeOnCollinearStrip,
  planarGraphPrevHalfEdgeOnCollinearStrip,
} from "geom/planarGraph";
import {
  polyLine2EdgeEval,
  polyLine2Explode,
  polyLine2Extend,
  polyLine2MergeCoincidentVertices,
  polyLine2Reverse,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { ray2Project } from "geom/ray2";
import { vec2DistSquared, vec2Dot, vec2Scale } from "geom/vec2";
import { solidWallGaps } from "../elements/walls/solidWallCrossSectionUtils";
import { getWallPath } from "../elements/walls/wall";
import { solidWallFramingLayer, solidWallLayerWithFallback } from "../elements/walls/wallLayers";
import { isSolidWall } from "../elements/walls/wallTypeGuards";

const REMOVE_OPP_AND_ADJ_DIMS = true;

interface DimLinePt<T> {
  readonly type: T;
  readonly x: number;
  readonly y: number;
  readonly originHalfEdge: number;
}
type DimLinePtWall = DimLinePt<"WALL">;
interface DimLinePtOpening extends DimLinePt<"OPENING"> {
  readonly widthOffset: number;
  readonly wallCenter: number; // door.center + accumulated dimLength at that point in time.
}

export function getInteriorDimensionStripsByString(
  building: Pick<Building, "planarGraphs" | "roomsByLevel" | "settings" | "walls" | "dimensionBehavior">,
  levelId: string,
): { dimStrips: PolyLine2[][]; dimStripToHalfEdgeMap: number[][][] } {
  const { planarGraphs, walls: wallTable } = building;
  const wallConfig = building.settings.wall;
  const plansetConfig = building.settings.planset;
  const planarGraph = planarGraphs[levelId].planarGraph;
  const walls = wallTable[levelId];
  const { vertexHalfEdges } = planarGraph;
  const visitedHalfEdges = {};
  const dimensionBehavior = building.dimensionBehavior[levelId];
  const exteriorDimensionDatum = building.settings.planset.exteriorDimensionDatum;
  const exteriorEdgeWallDatum =
    exteriorDimensionDatum === WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING
      ? WallLayerType.STRUCTURE
      : WallLayerType.SUBSTRATE;

  const halfEdgeDimStrip: { [key: number]: number[] } = {}; // half edge, dim strip (array of half edge)
  const dimStripHalfEdges: number[][] = [];

  const halfEdgeIsInteriorWall = (halfEdge: number) => {
    if (halfEdge === -1) {
      return false;
    }
    const wallIndex = walls.halfEdgeToWall[halfEdge];
    const wall = walls.walls[wallIndex];
    const path = getWallPath(wall);

    return (
      path.leftRoom &&
      isInteriorRoomEnclosure[path.leftRoom.attributes.roomEnclosure] &&
      path.rightRoom &&
      isInteriorRoomEnclosure[path.rightRoom.attributes.roomEnclosure]
    );
  };

  const halfEdgeDimsDisabled = (halfEdge: number) => {
    return dimensionBehavior[halfEdge]?.find((x) => x.item.disableInteriorAuto) !== undefined;
  };

  const halfEdgeIsDisabledViaRoom = (halfEdge: number) => {
    const wallIndex = walls.halfEdgeToWall[halfEdge];
    const wall = walls.walls[wallIndex];
    const path = getWallPath(wall);
    const leftRoom = path.leftRoom;
    if (leftRoom && leftRoom.attributes.disableInteriorDimensions) {
      return true;
    }
    const rightRoom = path.rightRoom;
    if (rightRoom && rightRoom.attributes.disableInteriorDimensions) {
      return true;
    }

    return false;
  };

  const halfEdgeIsDimOriented = (halfEdge: number) => {
    const edge = planarGraphHalfEdgeToLine2(planarGraph, halfEdge);
    const edgeOriented = getOrientedDimLine(edge);
    return vec2DistSquared(edge[0], edge[1], edgeOriented[0], edgeOriented[1]) > DIST_EPSILON_SQUARED;
  };

  // Create strips of dims and mark potential redundant dims that can be removed later.
  for (const vertexHalfEdge of vertexHalfEdges) {
    for (const halfEdgeIndex of vertexHalfEdge) {
      const wallIndex = walls.halfEdgeToWall[halfEdgeIndex];
      const wall = walls.walls[wallIndex];

      if (
        visitedHalfEdges[halfEdgeIndex] ||
        !halfEdgeIsInteriorWall(halfEdgeIndex) ||
        wall.wallType === WallType.VOID
      ) {
        continue;
      }

      // Trace from this vertex to the next edge with the same normal to create a strip of dims.
      let traceHalfEdge = halfEdgeIndex;
      const dimStrip: number[] = [];
      dimStripHalfEdges.push(dimStrip);

      while (traceHalfEdge !== -1 && halfEdgeIsInteriorWall(traceHalfEdge)) {
        dimStrip.push(traceHalfEdge);
        halfEdgeDimStrip[traceHalfEdge] = dimStrip;
        visitedHalfEdges[traceHalfEdge] = true;
        traceHalfEdge = planarGraphNextHalfEdgeOnCollinearStrip(planarGraph, traceHalfEdge);
      }

      traceHalfEdge = planarGraphPrevHalfEdgeOnCollinearStrip(planarGraph, halfEdgeIndex);
      while (traceHalfEdge !== -1 && halfEdgeIsInteriorWall(traceHalfEdge)) {
        dimStrip.unshift(traceHalfEdge);
        halfEdgeDimStrip[traceHalfEdge] = dimStrip;
        visitedHalfEdges[traceHalfEdge] = true;
        traceHalfEdge = planarGraphPrevHalfEdgeOnCollinearStrip(planarGraph, traceHalfEdge);
      }
    }
  }

  const ret: PolyLine2[][] = [];
  const dimStripToHalfEdgeMap: number[][][] = [];
  const vis: boolean[] = [];

  dimStripHalfEdges.sort((a, b) => b.length - a.length);

  for (const dimStrip of dimStripHalfEdges) {
    const dimLines: PolyLine2[] = [];
    const dimLineHalfEdges: number[][] = [];
    let dimLength = 0;

    // We accumulate all potential dimLine points into this.  We later filter out door center points that are close to wall start/end.
    let dimLine: (DimLinePtWall | DimLinePtOpening)[] = [];
    let firstOffset: number | undefined;
    let secondOffset: number | undefined;
    const skippedHalfEdges: number[] = [];

    for (const halfEdge of dimStrip) {
      vis[halfEdge] = true;

      if (halfEdgeDimsDisabled(halfEdge) || halfEdgeDimsDisabled(planarGraphAdjacentHalfEdge(halfEdge))) {
        continue;
      }

      if (halfEdgeIsDisabledViaRoom(halfEdge)) {
        continue;
      }

      if (REMOVE_OPP_AND_ADJ_DIMS) {
        const adj = planarGraphAdjacentHalfEdge(halfEdge);
        const opp = findOppositeHalfEdge(planarGraph, adj);

        if (vis[adj] || vis[opp]) {
          continue;
        }
      }

      const v0 = planarGraph.halfEdges[halfEdge * 2];
      const v1 = planarGraph.halfEdges[halfEdge * 2 + 1];

      const halfEdgeOffset = dimLength;

      const lookingForStart = dimLine.length === 0;
      if (lookingForStart) {
        // determine whether and how far we should adjust the start of the
        // segment to account for the wall thickness
        const [hasSolidWall, wallOffset] = nonParallelSolidWallAtVertex(
          planarGraph,
          walls,
          wallConfig,
          v0,
          halfEdge,
          exteriorEdgeWallDatum,
        );

        if (hasSolidWall) {
          firstOffset = wallOffset;
          const [x, y] = planarGraphHalfEdgeStart(planarGraph, halfEdge);
          dimLine.push({ type: "WALL", x, y, originHalfEdge: halfEdge });
          dimLength += planarGraphHalfEdgeLength(planarGraph, halfEdge);
        }
      }

      const lookingForEnd = firstOffset !== undefined;
      if (lookingForEnd) {
        const wallId = walls.halfEdgeToWall[halfEdge];
        const wall = walls.walls[wallId];

        if (!isSolidWall(wall)) {
          continue;
        }

        // determine whether and how far we should adjust the start of the
        // segment to account for the wall thickness
        const [hasSolidWall, wallOffset] = nonParallelSolidWallAtVertex(
          planarGraph,
          walls,
          wallConfig,
          v1,
          halfEdge,
          exteriorEdgeWallDatum,
        );

        const wallPath = getWallPath(wall);
        const leftSide = wallPath.halfEdgeIndex !== halfEdge;
        const { leftEdge, rightEdge } = solidWallFramingLayer(wall);
        const edge = leftSide ? leftEdge : rightEdge;

        const [ox, oy] = planarGraphHalfEdgeStart(planarGraph, halfEdge);
        const [tx, ty] = planarGraphHalfEdgeTangent(planarGraph, halfEdge);

        // don't dim doors in small walls
        if (wallPath.length > plansetConfig.minWallLength) {
          const gaps: StartSortedWallGaps = solidWallGaps({
            wall,
            isLeftSide: leftSide,
            normalized: false,
            excludeUndimensionedOpenings: true,
          });

          for (const gap of gaps.gaps) {
            const [sx, sy] = polyLine2EdgeEval(edge, 0, gap.center);
            const [x, y] = ray2Project(ox, oy, tx, ty, sx, sy);

            dimLine.push({
              type: "OPENING",
              x,
              y,
              widthOffset: (gap.end - gap.start) * 0.5,
              wallCenter: halfEdgeOffset + gap.center,
              originHalfEdge: halfEdge,
            });
          }
        }

        if (hasSolidWall) {
          secondOffset = wallOffset;

          const [x, y] = planarGraphHalfEdgeEnd(planarGraph, halfEdge);
          dimLine.push({ type: "WALL", x, y, originHalfEdge: halfEdge });
          dimLength += planarGraphHalfEdgeLength(planarGraph, halfEdge);
        }
      }

      if (secondOffset !== undefined) {
        // When the very first half-edge of a strip is a void wall and the
        // resulting dimension has exactly two wall points (start and end), the
        // void wall's length is incorrectly included. Advance the start to the
        // void wall's end vertex (i.e. the next solid wall's start) so the
        // dimension only measures the solid section.
        //
        // This only applies to void walls that open the strip. Void walls in
        // the middle of a strip (e.g. spanning an Entry/Stairs between two
        // solid segments) legitimately anchor subsequent chain dimensions and
        // must keep their original start point.

        // There's probably a general fix here, but we're not sure exactly what
        // the desired outcomes are so this is good enough for now.
        if (dimLine.length === 2 && dimLine[0].type === "WALL") {
          const startHE = dimLine[0].originHalfEdge;
          const startWall = walls.walls[walls.halfEdgeToWall[startHE]];
          if (!isSolidWall(startWall) && dimStrip[0] === startHE) {
            const [x, y] = planarGraphHalfEdgeEnd(planarGraph, startHE);
            dimLine[0] = { ...dimLine[0], x, y };

            const nextHE = dimLine[1].originHalfEdge;
            const nextV0 = planarGraph.halfEdges[nextHE * 2];
            const [, newOffset] = nonParallelSolidWallAtVertex(
              planarGraph,
              walls,
              wallConfig,
              nextV0,
              nextHE,
              exteriorEdgeWallDatum,
            );
            firstOffset = newOffset;
          }
        }

        const x0 = dimLine[0].x;
        const y0 = dimLine[0].y;
        const x1 = dimLine[dimLine.length - 1].x;
        const y1 = dimLine[dimLine.length - 1].y;

        const filteredDimLine = dimLine.filter((pt) => {
          if (pt.type === "WALL") {
            return true;
          }

          const finalThreshold =
            (plansetConfig.minJambLength + pt.widthOffset) * (plansetConfig.minJambLength + pt.widthOffset);
          return (
            vec2DistSquared(x0, y0, pt.x, pt.y) > finalThreshold && vec2DistSquared(x1, y1, pt.x, pt.y) > finalThreshold
          );
        });
        let c = 0;
        let pl = polyLine2New(filteredDimLine.length * 2);
        const filteredPolyLineHalfEdges: Set<number> = new Set(skippedHalfEdges);

        for (const pt of filteredDimLine) {
          filteredPolyLineHalfEdges.add(pt.originHalfEdge);
          pl[c++] = pt.x;
          pl[c++] = pt.y;
        }

        polyLine2Extend(pl, firstOffset !== undefined ? -firstOffset : undefined, secondOffset);

        if (halfEdgeIsDimOriented(halfEdge)) {
          polyLine2Reverse(pl);
        }

        pl = polyLine2MergeCoincidentVertices(pl);

        const explodedPolylines = polyLine2Explode(pl);

        dimLines.push(...explodedPolylines);
        dimLine = [];
        firstOffset = undefined;
        secondOffset = undefined;
        dimLength = 0;
        dimLineHalfEdges.push(...explodedPolylines.map(() => [...filteredPolyLineHalfEdges]));
      } else {
        skippedHalfEdges.push(halfEdge);
      }
    }
    if (dimLines.length) {
      ret.push(dimLines);
      dimStripToHalfEdgeMap.push(dimLineHalfEdges);
    }
  }
  return { dimStrips: ret, dimStripToHalfEdgeMap: dimStripToHalfEdgeMap };
}

// "vertical" means the y component of the dimension direction is greater than the x component
function dimensionPathIsHorizontal(tangentX: number, tangentY: number): boolean {
  return Math.abs(tangentY) - ANGLE_EPSILON < Math.abs(tangentX);
}

function nonParallelSolidWallAtVertex(
  planarGraph: PlanarGraph,
  walls: WallSystem,
  wallSettings: WallSettings,
  vertex: number,
  halfEdge: number,
  exteriorEdgeWallDatum: WallLayerType,
): [boolean, number] {
  const [tx, ty] = planarGraphHalfEdgeTangent(planarGraph, halfEdge);
  const [nx, ny] = planarGraphHalfEdgeNormal(planarGraph, halfEdge);

  // look at the half edges emerging from the end vertex - do any of them host a wall?

  for (const vHalfEdge of planarGraph.vertexHalfEdges[vertex]) {
    const [hetx, hety] = planarGraphHalfEdgeTangent(planarGraph, vHalfEdge);

    // TODO measure to the end of the baseline of the current wall on this side

    // if the junction contains a wall that is not perp, or parallel to
    // the given half edge, we skip it. this implies the vertex is measure to its
    // center line
    const dot = Math.abs(vec2Dot(tx, ty, hetx, hety));

    if (dot > 1e-3 && Math.abs(dot - 1) > 1e-3) {
      return [true, 0];
    }
  }

  const halfEdgeWallIndex = walls.halfEdgeToWall[halfEdge];
  const halfEdgeWall = walls.walls[halfEdgeWallIndex];

  const isHorizontal = dimensionPathIsHorizontal(tx, ty);
  let parallelSolidWall: SolidWall | undefined;

  for (const vHalfEdge of planarGraph.vertexHalfEdges[vertex]) {
    const [henx, heny] = planarGraphHalfEdgeNormal(planarGraph, vHalfEdge);

    const wallIndex = walls.halfEdgeToWall[vHalfEdge];
    const wall = walls.walls[wallIndex];

    if (wall.wallType !== WallType.SOLID) {
      continue;
    }

    if (
      vec2DistSquared(henx, heny, nx, ny) < DIST_EPSILON_SQUARED ||
      vec2DistSquared(henx, heny, -nx, -ny) < DIST_EPSILON_SQUARED
    ) {
      if (vHalfEdge !== halfEdge && vHalfEdge !== planarGraphAdjacentHalfEdge(halfEdge)) {
        parallelSolidWall = wall;
      }
      continue;
    }

    // the wall's path can be oriented with the outgoing halfedge or the incoming halfedge
    const wallPath = getWallPath(wall);
    const wallFollowsHalfEdgeDir = wallPath.halfEdgeIndex === vHalfEdge;
    const wallFramingMass = solidWallFramingLayer(wall);

    // extract the left or right kind/offset oriented with the outgoing halfedge
    const leftRoomKind = wallFollowsHalfEdgeDir ? wallPath.leftRoomKind : wallPath.rightRoomKind;
    const rightRoomKind = wallFollowsHalfEdgeDir ? wallPath.rightRoomKind : wallPath.leftRoomKind;

    const leftOffset = wallFollowsHalfEdgeDir ? wallFramingMass.leftOffset : wallFramingMass.rightOffset;
    const rightOffset = wallFollowsHalfEdgeDir ? wallFramingMass.rightOffset : wallFramingMass.leftOffset;

    // When interior dims run to an exterior wall, always snap to exterior (or interior) face of exterior wall.
    if (leftRoomKind !== RoomKind.INTERIOR) {
      const exteriorMass = solidWallLayerWithFallback(wall, exteriorEdgeWallDatum);
      const leftExteriorOffset = wallFollowsHalfEdgeDir ? exteriorMass.leftOffset : exteriorMass.rightOffset;

      // -leftExteriorOffset because hen runs to the right of the outgoing he
      const [exteriorX, exteriorY] = vec2Scale(henx, heny, -leftExteriorOffset);
      return [true, vec2Dot(tx, ty, exteriorX, exteriorY)];
    }

    if (rightRoomKind !== RoomKind.INTERIOR) {
      const exteriorMass = solidWallLayerWithFallback(wall, exteriorEdgeWallDatum);
      const rightExteriorOffset = wallFollowsHalfEdgeDir ? exteriorMass.rightOffset : exteriorMass.leftOffset;

      const [exteriorX, exteriorY] = vec2Scale(henx, heny, rightExteriorOffset);
      return [true, vec2Dot(tx, ty, exteriorX, exteriorY)];
    }

    // Regardless of whether the junction is L or T, interior dims should be taken from the RIGHT
    // side of the wall in horizontal dims strings

    const [leftx, lefty] = vec2Scale(henx, heny, -leftOffset);
    const [rightx, righty] = vec2Scale(henx, heny, rightOffset);

    if (isHorizontal) {
      if (rightx < 0) {
        return [true, vec2Dot(tx, ty, rightx, righty)];
      }

      return [true, vec2Dot(tx, ty, leftx, lefty)];
    }

    // Regardless of whether the junction is L or T, interior dims should be taken from the BOTTOM
    // side of the wall in vertical dim strings
    if (righty < 0) {
      return [true, vec2Dot(tx, ty, rightx, righty)];
    }

    return [true, vec2Dot(tx, ty, leftx, lefty)];
  }

  // TODO measure to the end of the baseline of the current wall

  if (planarGraph.vertexHalfEdges[vertex].length <= 2) {
    if (parallelSolidWall) {
      if (halfEdgeWall.wallType === WallType.VOID) {
        return [true, wallSettings.interiorWallOffset];
      }

      const halfEdgeWallFramingMass = solidWallFramingLayer(halfEdgeWall);
      const parallelFramingMass = solidWallFramingLayer(parallelSolidWall);

      // two parallel solid walls with differing thicknesses need to be dimensioned at their intersection
      if (parallelFramingMass.thickness !== halfEdgeWallFramingMass.thickness) {
        return [true, 0];
      }
      // two parallel solid walls with same thicknesses need to be skipped
      return [false, 0];
    }

    // Deal with "corner" with one wall being a void wall.
    return [true, wallSettings.interiorWallOffset];
  }

  return [false, 0];
}

function findOppositeHalfEdge(planarGraph: PlanarGraph, halfEdgeIndex: number): number {
  const { halfEdgeParentFace, faceHalfEdges } = planarGraph;
  const [nx, ny] = planarGraphHalfEdgeTangent(planarGraph, halfEdgeIndex);
  const absNx = Math.abs(nx);
  const absNy = Math.abs(ny);
  const [edgeX1, edgeY1] = planarGraphHalfEdgeStart(planarGraph, halfEdgeIndex);
  const [edgeX2, edgeY2] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeIndex);
  const halfEdgeStart = vec2Dot(edgeX1, edgeY1, absNx, absNy);
  const halfEdgeEnd = vec2Dot(edgeX2, edgeY2, absNx, absNy);
  const h1 = Math.min(halfEdgeStart, halfEdgeEnd);
  const h2 = Math.max(halfEdgeStart, halfEdgeEnd);
  const faceIndex = halfEdgeParentFace[halfEdgeIndex];
  const faceEdges = faceHalfEdges[faceIndex];

  for (const faceHalfEdge of faceEdges) {
    if (faceHalfEdge === halfEdgeIndex) {
      continue;
    }
    const [fnx, fny] = planarGraphHalfEdgeTangent(planarGraph, faceHalfEdge);
    if (Math.abs(vec2Dot(nx, ny, fnx, fny)) >= 1 - DIST_EPSILON) {
      const [candidateX1, candidateY1] = planarGraphHalfEdgeStart(planarGraph, faceHalfEdge);
      const [candidateX2, candidateY2] = planarGraphHalfEdgeEnd(planarGraph, faceHalfEdge);
      const start = vec2Dot(candidateX1, candidateY1, absNx, absNy);
      const end = vec2Dot(candidateX2, candidateY2, absNx, absNy);
      if (Math.abs(h1 - Math.min(start, end)) < DIST_EPSILON && Math.abs(h2 - Math.max(start, end)) < DIST_EPSILON) {
        return faceHalfEdge;
      }
    }
  }

  return -1;
}
